import { useEffect, useState } from "react";
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { apiCall } from '../../utils/apiClient';
import { login, logout } from '../../store/slices/authSlice';
import { fetchWishlist } from '../../store/slices/wishlistSlice';
import { colors } from "../../theme/colors";
import "./LoginStyle.css";

const bannerImages = [
  "https://images.unsplash.com/photo-1615874959474-d609969a20ed?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1600121848594-d8644e57abab?auto=format&fit=crop&w=1600&q=80",
  "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1600&q=80",
];

const formatDate = (value) => {
  if (!value) return 'Not available';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not available';
  return parsed.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const AuthModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { isAuthenticated, user, role } = useSelector((state) => state.auth);
  const hasValidSession = Boolean(isAuthenticated && user?.token);
  const [bg, setBg] = useState(0);
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [brokerReference, setBrokerReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accountStats, setAccountStats] = useState({
    totalOrders: 0,
    deliveredOrders: 0,
    activeTenures: 0,
    pendingAmount: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setBg((prev) => (prev + 1) % bannerImages.length);
    }, 4000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!open || !hasValidSession || !user?.token) {
      setAccountStats({
        totalOrders: 0,
        deliveredOrders: 0,
        activeTenures: 0,
        pendingAmount: 0,
      });
      setStatsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const orders = await apiCall({ method: 'GET', url: '/orders/me' });
        if (cancelled) return;

        const normalizedOrders = Array.isArray(orders) ? orders : [];
        setAccountStats({
          totalOrders: normalizedOrders.length,
          deliveredOrders: normalizedOrders.filter((order) => order?.status === 'Delivered').length,
          activeTenures: normalizedOrders.filter((order) => Number(order?.remainingTenureAmount || 0) > 0).length,
          pendingAmount: normalizedOrders.reduce(
            (sum, order) => sum + Number(order?.remainingTenureAmount || order?.pendingAmount || 0),
            0,
          ),
        });
      } catch {
        if (!cancelled) {
          setAccountStats({
            totalOrders: 0,
            deliveredOrders: 0,
            activeTenures: 0,
            pendingAmount: 0,
          });
        }
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
        }
      }
    };

    loadStats();

    return () => {
      cancelled = true;
    };
  }, [open, hasValidSession, user?.token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Please fill in all fields');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const url = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { username, password }
        : { username, password, brokerReference: brokerReference.trim() };
      const result = await apiCall({
        method: 'POST',
        url,
        data: payload,
      });

      const resolvedUser = result?.user;
      if (!resolvedUser) {
        throw new Error('Invalid response');
      }

      dispatch(login({ user: resolvedUser, role: resolvedUser.role || 'user' }));
      await dispatch(fetchWishlist());

      if (!isLogin) {
        setIsLogin(true);
      }

      onClose();
    } catch (err) {
      const message = err?.response?.data?.error || 'Authentication failed. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setUsername('');
    setPassword('');
    setBrokerReference('');
    setError('');
  };

  if (!open) return null;

  return createPortal(
    <div className="rm-overlay" onClick={onClose}>
      <div className={`rm-modal ${hasValidSession ? 'rm-modal-authenticated' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* CLOSE */}
        <button className="rm-close" onClick={onClose}>
          ✕
        </button>

        {/* LEFT ILLUSTRATION */}
        <div className="rm-left">
          <img src={bannerImages[bg]} alt="" />
        </div>

        {/* RIGHT CONTENT */}
        <div className="rm-right">
          <h2>{hasValidSession ? `Logged in as ${user?.username} (${role})` : isLogin ? 'Login to your account' : 'Create a new account'}</h2>

          {hasValidSession ? (
            <>
              <div className="rm-user-summary">
                <div className="rm-user-identity">
                  <span className="rm-user-badge">{role === 'admin' ? 'Admin account' : 'Customer account'}</span>
                  <p className="rm-user-name">{user?.username || 'HB Furniture user'}</p>
                </div>

                <div className="rm-user-meta-grid">
                  <div className="rm-user-meta-card">
                    <span>Member since</span>
                    <strong>{formatDate(user?.createdAt)}</strong>
                  </div>
                  <div className="rm-user-meta-card">
                    <span>Last login</span>
                    <strong>{formatDate(user?.lastLoginAt)}</strong>
                  </div>
                  <div className="rm-user-meta-card">
                    <span>Total logins</span>
                    <strong>{Number(user?.loginCount || 0)}</strong>
                  </div>
                  <div className="rm-user-meta-card">
                    <span>Pending amount</span>
                    <strong>Rs {Number(accountStats.pendingAmount || 0).toLocaleString('en-IN')}</strong>
                  </div>
                </div>

                <div className="rm-user-stats-row">
                  <div className="rm-user-stat-pill">
                    <span>Total orders</span>
                    <strong>{statsLoading ? '...' : accountStats.totalOrders}</strong>
                  </div>
                  <div className="rm-user-stat-pill">
                    <span>Delivered</span>
                    <strong>{statsLoading ? '...' : accountStats.deliveredOrders}</strong>
                  </div>
                  <div className="rm-user-stat-pill">
                    <span>Active tenures</span>
                    <strong>{statsLoading ? '...' : accountStats.activeTenures}</strong>
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="rm-btn secondary"
                onClick={() => {
                  dispatch(logout());
                  onClose();
                }}
                style={{ backgroundColor: colors.secondary, color: colors.white }}
              >
                Logout
              </button>
            </>
          ) : (
            <>

          <form onSubmit={handleSubmit}>
            <div className="rm-input">
              <input
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>

            <div className="rm-input">
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            {!isLogin ? (
              <div className="rm-input rm-input-note-wrap">
                <input
                  type="text"
                  placeholder="Ref by broker name (optional)"
                  value={brokerReference}
                  onChange={(e) => setBrokerReference(e.target.value)}
                />
                <p className="rm-input-note">
                  Add broker reference name (if any) to get more discount.
                </p>
              </div>
            ) : null}

            <button
              type="submit"
              className="rm-btn primary"
              style={{
                backgroundColor: colors.primary,
                color: colors.white,
                cursor: username && password && !loading ? 'pointer' : 'not-allowed'
              }}
              disabled={!username || !password || loading}
            >
              {loading ? 'Please wait...' : isLogin ? 'Login' : 'Create Account'}
            </button>
          </form>

          {error ? <p className="rm-error">{error}</p> : null}

          <button
            type="button"
            className="rm-btn secondary"
            onClick={toggleMode}
            style={{
              backgroundColor: colors.secondary,
              color: colors.white,
              marginTop: '12px'
            }}
          >
            {isLogin ? 'Create Account' : 'Back to Login'}
          </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AuthModal;
